import { useState, useContext } from 'react';
import { AppContext } from '@/App';
import { Truck, Plus, Search, Filter, Building2, Package, Calendar, CheckCircle, Clock, AlertCircle } from 'lucide-react';

function CustomerOrders() {
  const { API } = useContext(AppContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Customer orders for aluminium products
  const orders = [
    { id: 'ORD-2025-001', customer: 'Tata Steel', product: 'Aluminium Cubes - Standard', quantity: 25000, unit: 'kg', orderDate: '2025-02-01', deliveryDate: '2025-02-15', status: 'In Progress', dispatchedQty: 15000, vehicleNo: 'MH-12-AB-1234' },
    { id: 'ORD-2025-002', customer: 'Steel Authority of India Limited (SAIL)', product: 'Aluminium Notchbar - 10kg', quantity: 50000, unit: 'kg', orderDate: '2025-02-03', deliveryDate: '2025-02-20', status: 'Pending', dispatchedQty: 0, vehicleNo: null },
    { id: 'ORD-2025-003', customer: 'Jindal Stainless Limited', product: 'Aluminium Shots - Fine', quantity: 10000, unit: 'kg', orderDate: '2025-01-28', deliveryDate: '2025-02-10', status: 'Completed', dispatchedQty: 10000, vehicleNo: 'GJ-05-CD-5678' },
    { id: 'ORD-2025-004', customer: 'Tata Steel', product: 'Aluminium Ingot - 98%', quantity: 5000, unit: 'kg', orderDate: '2025-02-05', deliveryDate: '2025-02-25', status: 'Pending', dispatchedQty: 0, vehicleNo: null },
    { id: 'ORD-2025-005', customer: 'Hindalco Industries', product: 'Aluminium Notchbar - 5kg', quantity: 30000, unit: 'kg', orderDate: '2025-02-02', deliveryDate: '2025-02-18', status: 'In Progress', dispatchedQty: 20000, vehicleNo: 'RJ-14-EF-9012' },
    { id: 'ORD-2025-006', customer: 'Vedanta Aluminium', product: 'Aluminium Cubes - Premium', quantity: 20000, unit: 'kg', orderDate: '2025-01-25', deliveryDate: '2025-02-08', status: 'Completed', dispatchedQty: 20000, vehicleNo: 'MP-09-GH-3456' },
    { id: 'ORD-2025-007', customer: 'Steel Authority of India Limited (SAIL)', product: 'Aluminium Shots - Coarse', quantity: 15000, unit: 'kg', orderDate: '2025-02-06', deliveryDate: '2025-02-22', status: 'Pending', dispatchedQty: 0, vehicleNo: null },
    { id: 'ORD-2025-008', customer: 'Jindal Stainless Limited', product: 'Aluminium Ingot - 97%', quantity: 8000, unit: 'kg', orderDate: '2025-02-04', deliveryDate: '2025-02-28', status: 'In Progress', dispatchedQty: 3000, vehicleNo: 'HR-26-IJ-7890' },
  ];

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.product.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-700';
      case 'In Progress': return 'bg-blue-100 text-blue-700';
      case 'Pending': return 'bg-yellow-100 text-yellow-700';
      case 'Cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Completed': return <CheckCircle size={14} />;
      case 'In Progress': return <Clock size={14} />;
      case 'Pending': return <AlertCircle size={14} />;
      default: return null;
    }
  };

  // Summary stats
  const totalOrders = orders.length;
  const completedOrders = orders.filter(o => o.status === 'Completed').length;
  const inProgressOrders = orders.filter(o => o.status === 'In Progress').length;
  const pendingOrders = orders.filter(o => o.status === 'Pending').length;
  const totalQuantity = orders.reduce((sum, o) => sum + o.quantity, 0);
  const dispatchedQuantity = orders.reduce((sum, o) => sum + o.dispatchedQty, 0);

  // Customer-wise summary
  const customerSummary = orders.reduce((acc, order) => {
    if (!acc[order.customer]) {
      acc[order.customer] = { orders: 0, quantity: 0 };
    }
    acc[order.customer].orders += 1;
    acc[order.customer].quantity += order.quantity;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="customer-orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Truck className="text-[#1a237e]" size={28} />
            Customer Orders
          </h1>
          <p className="text-gray-600 mt-1">Track and manage dispatch orders</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-[#1a237e] text-white rounded-lg hover:bg-[#283593] transition">
          <Plus size={18} />
          New Order
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className="bg-gradient-to-br from-[#1a237e] to-[#283593] rounded-xl p-4 text-white">
          <p className="text-sm opacity-80">Total Orders</p>
          <p className="text-3xl font-bold mt-1">{totalOrders}</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-4 text-white">
          <p className="text-sm opacity-80">Completed</p>
          <p className="text-3xl font-bold mt-1">{completedOrders}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <p className="text-sm opacity-80">In Progress</p>
          <p className="text-3xl font-bold mt-1">{inProgressOrders}</p>
        </div>
        <div className="bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl p-4 text-white">
          <p className="text-sm opacity-80">Pending</p>
          <p className="text-3xl font-bold mt-1">{pendingOrders}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
          <p className="text-sm opacity-80">Total Qty</p>
          <p className="text-3xl font-bold mt-1">{(totalQuantity / 1000).toFixed(0)} T</p>
        </div>
        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-4 text-white">
          <p className="text-sm opacity-80">Dispatched</p>
          <p className="text-3xl font-bold mt-1">{(dispatchedQuantity / 1000).toFixed(0)} T</p>
        </div>
      </div>

      {/* Customer Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {Object.entries(customerSummary).slice(0, 4).map(([customer, data]) => (
          <div key={customer} className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="text-[#1a237e]" size={18} />
              <h3 className="font-semibold text-gray-900 text-sm truncate">{customer}</h3>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{data.orders} orders</span>
              <span className="font-bold text-[#1a237e]">{(data.quantity / 1000).toFixed(1)} T</span>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search orders, customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] outline-none"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={18} className="text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] outline-none"
          >
            <option value="all">All Status</option>
            <option value="Pending">Pending</option>
            <option value="In Progress">In Progress</option>
            <option value="Completed">Completed</option>
          </select>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Order ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Product</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Quantity</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Dispatched</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Delivery Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Vehicle</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-3 text-sm font-mono text-[#1a237e] font-medium">{order.id}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Building2 size={16} className="text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">{order.customer}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Package size={16} className="text-gray-400" />
                      <span className="text-sm text-gray-700">{order.product}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-medium text-gray-900">{order.quantity.toLocaleString()} kg</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`font-medium ${order.dispatchedQty === order.quantity ? 'text-green-600' : 'text-blue-600'}`}>
                      {order.dispatchedQty.toLocaleString()} kg
                    </span>
                    <span className="text-xs text-gray-400 ml-1">
                      ({((order.dispatchedQty / order.quantity) * 100).toFixed(0)}%)
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Calendar size={14} />
                      {order.deliveryDate}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                    {order.vehicleNo || '-'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                      {getStatusIcon(order.status)}
                      {order.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default CustomerOrders;
